import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { publicAPI } from '../lib/api';
import { useSeo } from '../lib/seoHelpers';
import { Calendar, Clock, ArrowRight, Loader2 } from 'lucide-react';

const LANGUAGES = [
  { code: 'nl', label: 'Nederlands' },
  { code: 'en', label: 'English' },
];

const COPY = {
  nl: {
    heading: 'FoodFlows Blog',
    intro: 'Praktische artikelen voor restaurants over online bestellen, bezorging en groeien zonder commissie.',
    empty: 'Er zijn nog geen artikelen gepubliceerd.',
    read: 'Lees verder',
    minutes: 'min leestijd',
    all: 'Alle',
    cta: 'Start gratis',
  },
  en: {
    heading: 'FoodFlows Blog',
    intro: 'Practical articles for restaurants about online ordering, delivery and growing without commission.',
    empty: 'No articles have been published yet.',
    read: 'Read more',
    minutes: 'min read',
    all: 'All',
    cta: 'Get started',
  },
};

const formatDate = (iso, language) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(language === 'en' ? 'en-GB' : 'nl-BE',
      { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return String(iso).slice(0, 10); }
};

export const BlogPage = () => {
  // /blog toont Nederlands; /blog/en toont Engels.
  const { lang } = useParams();
  const navigate = useNavigate();
  const language = LANGUAGES.some(l => l.code === lang) ? lang : 'nl';
  const t = COPY[language];

  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: language === 'en' ? 'Blog — FoodFlows' : 'Blog — FoodFlows',
    description: t.intro,
    image: 'https://food-flows.com/og-image.png',
    canonical: `https://food-flows.com/blog${language === 'nl' ? '' : '/' + language}`,
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    publicAPI.getBlogPosts(language)
      .then(({ data }) => {
        if (cancelled) return;
        setPosts(data.posts || []);
        setCategories(data.categories || []);
      })
      .catch(() => { if (!cancelled) setPosts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [language]);

  const visible = activeCategory ? posts.filter(p => p.category === activeCategory) : posts;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/40 to-white" data-testid="blog-page">
      <header className="border-b border-gray-200/80 bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="blog-home-link">
            <div className="w-9 h-9 rounded-lg bg-[#1A4D2E] flex items-center justify-center text-white font-bold">F</div>
            <span className="font-heading text-lg font-bold text-gray-900 group-hover:text-[#1A4D2E] transition-colors">FoodFlows</span>
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-sm">
              {LANGUAGES.map(l => (
                <button
                  key={l.code}
                  onClick={() => navigate(l.code === 'nl' ? '/blog' : `/blog/${l.code}`)}
                  className={`px-2 py-1 rounded ${language === l.code ? 'font-semibold text-[#1A4D2E]' : 'text-gray-500 hover:text-gray-700'}`}
                  data-testid={`blog-lang-${l.code}`}
                >
                  {l.code.toUpperCase()}
                </button>
              ))}
            </div>
            <Link to="/register" className="px-4 py-2 rounded-full bg-[#1A4D2E] text-white text-sm font-medium hover:bg-[#143d24] transition-colors" data-testid="blog-cta-register">
              {t.cta}
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-12">
        <h1 className="font-heading text-4xl font-bold text-gray-900">{t.heading}</h1>
        <p className="text-gray-600 mt-3 max-w-2xl">{t.intro}</p>

        {categories.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-6">
            <button
              onClick={() => setActiveCategory(null)}
              className={`px-3 py-1.5 rounded-full text-sm border ${!activeCategory ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
            >
              {t.all}
            </button>
            {categories.map(c => (
              <button
                key={c}
                onClick={() => setActiveCategory(c)}
                className={`px-3 py-1.5 rounded-full text-sm border ${activeCategory === c ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
          </div>
        ) : visible.length === 0 ? (
          <p className="text-gray-500 py-20 text-center" data-testid="blog-empty">{t.empty}</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10" data-testid="blog-list">
            {visible.map(post => (
              <Link
                key={`${post.language}-${post.slug}`}
                to={`/blog/${post.language}/${post.slug}`}
                className="group bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow flex flex-col"
                data-testid={`blog-card-${post.slug}`}
              >
                {post.featured_image && (
                  <img src={post.featured_image} alt={post.title} className="w-full h-44 object-cover" loading="lazy" />
                )}
                <div className="p-5 flex flex-col flex-1">
                  {post.category && (
                    <span className="text-xs font-medium text-[#1A4D2E] bg-emerald-50 px-2 py-0.5 rounded-full self-start mb-2">
                      {post.category}
                    </span>
                  )}
                  <h2 className="font-heading text-lg font-bold text-gray-900 group-hover:text-[#1A4D2E] transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-gray-600 text-sm mt-2 flex-1">{post.excerpt}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-400 mt-4">
                    {post.published_at && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {formatDate(post.published_at, language)}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {post.reading_minutes} {t.minutes}
                    </span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[#1A4D2E] text-sm font-medium mt-3">
                    {t.read} <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
